<template>
  <div class="content-scrollable">
    <CommonCollapse :initial-open="true" title="Info" class="mb-1">
      <CommonProperty name="Reference ID">{{ name }}</CommonProperty>
      <CommonProperty name="Carrier">{{ carrier }}</CommonProperty>
      <CommonProperty name="Notes">
        <span v-if="notesDisplay" class="whitespace-pre-wrap">{{
          notesDisplay
        }}</span>
        <template v-else>–</template>
      </CommonProperty>
      <CommonProperty name="Status">
        <ShipmentStatusBadge v-if="status" :status="status" />
        <template v-else>–</template>
      </CommonProperty>
      <CommonProperty name="Start">{{ startAt }}</CommonProperty>
      <CommonProperty v-if="shipment?.ended_at" name="End">
        {{ endedAt }}
      </CommonProperty>
      <CommonProperty name="Scheduled End">{{ scheduledEndAt }}</CommonProperty>
      <CommonProperty name="Trackers">{{ deviceName }}</CommonProperty>
      <CommonProperty name="From">{{ fromAddress }}</CommonProperty>
      <CommonProperty name="To">{{ toAddress }}</CommonProperty>
      <CommonProperty name="Link" v-if="uuid">
        <Button
          size="sm"
          variant="destructive"
          class="!py-px !text-[10px]"
          @click="copyToClipboard"
        >
          Copy Link
        </Button>
      </CommonProperty>
    </CommonCollapse>
    <ShipmentExceptionEventsSection />
  </div>
</template>

<script>
import * as _ from 'lodash';
import { useClipboard } from '@vueuse/core';
import CommonProperty from '@/components/_common/CommonProperty.vue';
import ShipmentStatusBadge from '@/components/shipment/shared/ShipmentStatusBadge.vue';
import { formatTime } from '@/filters';
import { nextTick } from 'vue';
import { setTitle } from '@/utils/title';
import { useToast } from 'vue-toastification';
import * as events from '@/events';
import CommonCollapse from '@/components/_common/CommonCollapse.vue';
import ShipmentExceptionEventsSection from '@/components/shipment/details/ShipmentExceptionEventsSection.vue';
import { Button } from '@gpx-spa/ui';
import { shipmentEndpointDisplayName } from '@/utils/shipment-endpoint-display';

const toast = useToast();

export default {
  components: {
    CommonProperty,
    ShipmentStatusBadge,
    CommonCollapse,
    ShipmentExceptionEventsSection,
    Button,
  },

  props: {
    shipment: {
      type: Object,
      default: null,
    },
  },

  computed: {
    id() {
      return +this.$route.params.id;
    },
    uuid() {
      return _.get(this.shipment, 'uuid', null);
    },
    uuidLink() {
      return `${window.location.origin}/shipments/track/${this.uuid}`;
    },
    name() {
      return _.get(this.shipment, 'name', '-');
    },
    deviceName() {
      const devices = _.get(this.shipment, 'devices', []);
      const names = _.compact(
        (Array.isArray(devices) ? devices : []).map((d) => _.get(d, 'name')),
      );
      if (names.length > 0) return names.join(', ');
      const ids = _.get(this.shipment, 'device_ids', []);
      if (Array.isArray(ids) && ids.length > 0) {
        return ids.map((id) => `#${id}`).join(', ');
      }
      return '-';
    },
    notesDisplay() {
      const n = _.get(this.shipment, 'notes');
      if (n == null || String(n).trim() === '') {
        return '';
      }
      return String(n);
    },
    carrier() {
      const carrier = _.get(this.shipment, 'carrier', null);
      return _.defaultTo(carrier, '-');
    },
    status() {
      return _.get(this.shipment, 'status', null);
    },
    startAt() {
      const startAt = _.get(this.shipment, 'start_at', null);
      if (startAt) {
        return formatTime(startAt, 'MMM d, yyyy h:mm aa');
      }
      return '-';
    },
    endedAt() {
      const endAt = _.get(this.shipment, 'ended_at', null);
      if (endAt) {
        return formatTime(endAt, 'MMM d, yyyy h:mm aa');
      }
      return '-';
    },
    scheduledEndAt() {
      const endAt = _.get(this.shipment, 'end_at', null);
      if (endAt) {
        return formatTime(endAt, 'MMM d, yyyy h:mm aa');
      }
      return '-';
    },
    fromAddress() {
      return shipmentEndpointDisplayName(this.shipment, 'from');
    },
    toAddress() {
      return shipmentEndpointDisplayName(this.shipment, 'to');
    },
  },

  watch: {
    shipment: {
      handler(val) {
        if (!val) {
          return;
        }
        const name = _.get(val, 'name');
        setTitle(
          name != null && name !== ''
            ? `Shipment ${name}`
            : `Shipment #${this.id}`,
        );
        events.trigger(events.names.SHIPMENT_VIEWED, val);
      },
    },
  },

  created() {
    const name = _.get(this.shipment, 'name');
    setTitle(
      name != null && name !== '' ? `Shipment ${name}` : `Shipment #${this.id}`,
    );
  },

  mounted() {
    nextTick(() => {
      if (this.shipment) {
        events.trigger(events.names.SHIPMENT_VIEWED, this.shipment);
      }
    });
  },

  methods: {
    copyToClipboard() {
      const { copy, copied } = useClipboard({ source: this.uuidLink });
      copy();
      if (copied) {
        toast.success('Copied!', { timeout: 800 });
      }
    },
    handleGoToEdit() {
      this.$router.push({ name: 'ShipmentEditView', params: { id: this.id } });
    },
  },
};
</script>
